/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { TranslationCreationSteps } from 'src/translation/components/TranslationCreationPage';

export type TranslationFooterProps = {
    onNext: () => void;
    onDismiss: () => void;
    onSubmit: () => void;
    onPrevious?: () => void;
    isReturningUser: boolean;
    isNextDisabled?: boolean;
    isLoading?: boolean;
    activeStep: TranslationCreationSteps;
    isDiscardDisabled?: boolean;
};

const messages = defineMessages({
    back: {
        id: 'translation.translationToolFooter.back',
        defaultMessage: 'Back'
    },
    next: {
        id: 'translation.translationToolFooter.next',
        defaultMessage: 'Next'
    },
    startTranslationJob: {
        id: 'translation.translationToolFooter.startTranslationJob',
        defaultMessage: 'Start translation job'
    },
    discard: {
        id: 'translation.translationToolFooter.discard',
        defaultMessage: 'Discard'
    }
});

const { styleComponent } = getStylistV2('TranslationToolFooter');
const modalTheme = getScopedTheme('ProjectCreationModalBase');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const translationToolFooterTheme = getScopedTheme('TranslationToolFooter');

const Wrapper = styleComponent<IStackProps>(Stack)('Wrapper', () => ({
    borderTop: borderStyle,
    width: '100%',
    backgroundColor: translationToolFooterTheme.backgroundColor
}));

export const TranslationToolFooter = (props: TranslationFooterProps) => {
    const {
        onNext,
        onDismiss,
        activeStep,
        onPrevious,
        onSubmit,
        isNextDisabled = false,
        isReturningUser,
        isLoading = false,
        isDiscardDisabled
    } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();

    const isBackBtnHidden =
        activeStep === TranslationCreationSteps.InitConfiguration ||
        (isReturningUser && activeStep === TranslationCreationSteps.BasicInformation);
    const isNextBtnHidden = activeStep === TranslationCreationSteps.ReviewAndFinish;
    const isSubmitBtnDisabled = activeStep !== TranslationCreationSteps.ReviewAndFinish;

    return (
        <Wrapper horizontal={isLargeOrGreater} horizontalAlign="space-between" tokens={{ padding: 16, childrenGap: 6 }}>
            <Stack wrap horizontal={isLargeOrGreater} tokens={{ childrenGap: 8 }}>
                {!isBackBtnHidden && onPrevious && (
                    <DefaultButton onClick={onPrevious} disabled={isLoading}>
                        {localize(messages.back)}
                    </DefaultButton>
                )}

                {!isNextBtnHidden && (
                    <PrimaryButton disabled={isNextDisabled || !onNext || isLoading} onClick={onNext}>
                        {localize(messages.next)}
                    </PrimaryButton>
                )}
            </Stack>

            <Stack horizontal={isLargeOrGreater} tokens={{ childrenGap: 8 }}>
                <PrimaryButton disabled={isSubmitBtnDisabled || !onSubmit || isLoading} onClick={onSubmit}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        <LuisText>{localize(messages.startTranslationJob)}</LuisText>
                        {isLoading && <Spinner />}
                    </Stack>
                </PrimaryButton>

                <DefaultButton disabled={!onDismiss || isLoading || isDiscardDisabled} onClick={onDismiss}>
                    {localize(messages.discard)}
                </DefaultButton>
            </Stack>
        </Wrapper>
    );
};
